import { EventEmitter } from '@angular/core';

import { MatDialog } from '@angular/material/dialog';
import { Svg, G, Rect, Element as SvgElement, ClipPath, Polygon, Shape
       } from '@svgdotjs/svg.js';
import { ClickInfo } from 'app/controls/image-cropper/drag-sensor';
import { DeletePlaceholderDialogComponent
       } from 'app/dialogs/delete-placeholder-dialog/delete-placeholder-dialog.component'; // eslint-disable-line
import { Button } from 'app/dialogs/dialog-buttons.component';
import { GalleryService } from 'app/gallery/gallery.service';
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { DesignStepService
       } from 'collage-designer/design-step/design-step.service';
import { StageConstants } from 'core/constants';
import { ErrorService } from 'core/error/error.service';
import { LoggingService } from 'core/logging/logging.service';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { StageUtils } from 'core/utils.stage';
import { PlaceholderContentService } from './placeholder-content.service';
import { PlaceholderService } from './placeholder.service';

export enum PlaceholderType
{
  Image,
  Text,
}

export enum MarkerType
{
  None,
  Hover,
  Edit,
  Highlight,
  Drop,
}

enum MarkerStyle
{
  Border,
  Transparent,
}

class MarkerStack
{
  private _hover: boolean = false;
  private _edit: boolean = false;
  private _highlight: boolean = false;
  private _drop: boolean = false;
  private _dropshroud: boolean = false;

  constructor(private inner: Marker)
  {}

  public set hover(enabled: boolean)
  {
    this._hover = enabled;
    this.update_marker();
  }

  public set edit(enabled: boolean)
  {
    this._edit = enabled;
    this.update_marker();
  }

  public set highlight(enabled: boolean)
  {
    this._highlight = enabled;
    this.update_marker();
  }

  public set drop(enabled: boolean)
  {
    this._drop = enabled;
    this.update_marker();
  }

  public set dropshroud(enabled: boolean)
  {
    this._dropshroud = enabled;
    this.update_marker();
  }

  public redraw(): void
  {
    this.inner.redraw();
  }

  public withoutMarker(fn: () => void): void
  {
    this.inner.withoutMarker(fn);
  }

  private update_marker(): void
  {
    if (this._drop) {
      this.inner.type = MarkerType.Drop;
      return;
    }
    else if (this._dropshroud) {
      this.inner.type = MarkerType.None;
      return;
    }
    else if (this._edit) {
      this.inner.type = MarkerType.Edit;
      return;
    }
    else if (this._hover) {
      this.inner.type = MarkerType.Hover;
      return;
    }
    else if (this._highlight) {
      this.inner.type = MarkerType.Highlight;
      return;
    }

    this.inner.type = MarkerType.None;
  }
}

class Marker
{
  private outerWrapper: G | undefined = undefined;
  private innerWrapper: G | undefined = undefined;
  private markerType: MarkerType = MarkerType.None;
  private borderClips: ClipPath[] | undefined = undefined;
  private innerWrapperClipPath: string | undefined = undefined;

  constructor(private placeholderWrapper: SvgElement,
              private placeholderType: PlaceholderType)
  {}

  public get type(): MarkerType
  {
    return this.markerType;
  }

  public set type(markerType: MarkerType)
  {
    if (this.type === markerType) {
      return;
    }

    this.markerType = markerType;

    this.hide();
    if (markerType === MarkerType.None) {
      return;
    }
    this.show();
    this.draw();
  }

  public withoutMarker(fn: () => void): void
  {
    const markerType = this.markerType;
    this.type = MarkerType.None;
    try {
      fn();
    }
    finally {
      this.type = markerType;
    }
  }

  public redraw(): void
  {
    if (!this.visible) {
      return;
    }

    this.innerWrapper.clear();
    this.dropBorderClips();

    const transform =
        this.placeholderWrapper.parent().node.getAttribute('transform');
    if (transform) {
      this.innerWrapper.node.setAttribute('transform', transform);
    }
    else {
      this.innerWrapper.node.removeAttribute('transform');
    }

    if (this.innerWrapperClipPath) {
      this.innerWrapper.node.setAttribute(
          'clip-path', this.innerWrapperClipPath);
    }

    this.draw();
  }

  private draw(): void
  {
    const markerStyle =
        this.markerType === MarkerType.Hover && !this.isTextMarker
      ? MarkerStyle.Transparent
      : MarkerStyle.Border;

    if (markerStyle === MarkerStyle.Border) {
      this.drawBorderShape(this.innerWrapper);
    }
    else {
      this.innerWrapper.rect()
        .move(this.placeholderWrapper.x(), this.placeholderWrapper.y())
        .size(this.placeholderWrapper.width(),
              this.placeholderWrapper.height());
    }

    if (this.isTextMarker) {
      this.innerWrapper.addClass(StageConstants.CLASS_NAME_TEXT_MARKER);
      return;
    }

    switch (this.markerType) {
      case MarkerType.Hover:
        this.innerWrapper.addClass(
          StageConstants.CLASS_NAME_HOVER_MARKER);
        break;
      case MarkerType.Edit:
        this.innerWrapper.addClass(
          StageConstants.CLASS_NAME_EDIT_MARKER);
        break;
      case MarkerType.Highlight:
        this.innerWrapper.addClass(
          StageConstants.CLASS_NAME_HIGHLIGHT_MARKER);
        break;
      case MarkerType.Drop:
        this.innerWrapper.addClass(
          StageConstants.CLASS_NAME_DROP_TARGET_MARKER);
        break;
      default:
        break;
    }
  }

  private show(): void
  {
    if (this.visible) {
      return;
    }

    const container = this.placeholderWrapper.parent() as any;

    const sharedGroup = new G();
    sharedGroup.insertBefore(container);
    sharedGroup.add(container);

    const group = new G();
    group.insertAfter(container);

    const root = container.clone().clear();
    root.addTo(group);
    root.attr('pointer-events', 'none');
    root.node.removeAttribute('style');

    this.outerWrapper = group;
    this.innerWrapper = root;
    this.innerWrapperClipPath =
        root.node.getAttribute('clip-path') || undefined;
  }

  private hide(): void
  {
    if (this.outerWrapper !== undefined) {
      this.outerWrapper.remove();
      this.outerWrapper = undefined;

      const sharedGroup = this.placeholderWrapper.parent().parent();
      this.placeholderWrapper.parent().insertBefore(<any>sharedGroup);
      sharedGroup.remove();
    }

    this.dropBorderClips();

    this.innerWrapper = undefined;
    this.innerWrapperClipPath = undefined;
  }

  private dropBorderClips(): void
  {
    if (this.borderClips !== undefined) {
      this.borderClips.forEach(clip => clip.remove());
      this.borderClips = undefined;
    }
  }

  private get visible(): boolean
  {
    return this.outerWrapper !== undefined;
  }

  private get isTextMarker(): boolean
  {
    return this.placeholderType === PlaceholderType.Text &&
        (this.markerType === MarkerType.Edit ||
         this.markerType === MarkerType.Hover);
  }

  private drawBorderShape(innerMarkerWrapper: G): void
  {
    // pixels on the screen
    const borderScreenSize = this.isTextMarker
      ? StageConstants.TEXT_MARKER_BORDER_SCREEN_WIDTH
      : StageConstants.MARKER_BORDER_SCREEN_WIDTH;
    const borderScreenRadius =
        this.isTextMarker ? StageConstants.TEXT_MARKER_SCREEN_RADIUS : 0;

    const root = this.placeholderWrapper.root();
    const defs = root.defs();
    const clipPath = StageUtils.findClipPath(innerMarkerWrapper);
    innerMarkerWrapper.node.removeAttribute('clip-path');

    const clips: ClipPath[] = [];

    const shapeWithoutTransform = (shape: Shape): Shape => {
      const rawShape = shape.clone();
      rawShape.node.removeAttribute('transform');
      return rawShape;
    };

    const strokeAndClip = (el: Shape, clip: ClipPath): void => {
      // Stroke sits centered on the contour, so only the inner half of it is
      // visible, so we need to multiply it to see the desired size
      // after clipping
      const innerBorderStrokeFactor = 2;

      el.attr('vector-effect', 'non-scaling-stroke')
        .attr('stroke-width', borderScreenSize * innerBorderStrokeFactor)
        .clipWith(clip);
    };

    let placeholderShape: Shape;

    if (clipPath) {
      placeholderShape = clipPath.children().at(0).clone();
      placeholderShape.node.removeAttribute('style');
      placeholderShape.node.removeAttribute('class');

      const combined = [
        clipPath.attr('transform'),
        placeholderShape.attr('transform')
      ].filter(Boolean).join(' ');

      if (combined) {
        placeholderShape.attr('transform', combined);
      }

    } else {
      const ctm = innerMarkerWrapper.node.getScreenCTM();
      const unitsPerScreenPixel = 1 / Math.hypot(ctm.a, ctm.b);

      const visibleArea =
          StageUtils.getWrapperRegionWithinPartViewBox(this.placeholderWrapper);
      const outset =
          this.isTextMarker ? borderScreenSize * unitsPerScreenPixel : 0;

      placeholderShape = new Rect()
        .move(visibleArea.x - outset, visibleArea.y - outset)
        .size(visibleArea.width + 2 * outset, visibleArea.height + 2 * outset)
        .radius(borderScreenRadius * unitsPerScreenPixel + outset);
    }

    const borderContainer = new G();
    innerMarkerWrapper.add(borderContainer);

    // Map the stage viewBox corners into the border container's local space
    const viewbox = root.viewbox();
    const rootToLocal = borderContainer.node.getScreenCTM().inverse()
                            .multiply(root.node.getScreenCTM());

    const toLocalPoint = (x: number, y: number): string => {
      const lx = rootToLocal.a * x + rootToLocal.c * y + rootToLocal.e;
      const ly = rootToLocal.b * x + rootToLocal.d * y + rootToLocal.f;
      return `${lx},${ly}`;
    };

    const stagePoints = [
      toLocalPoint(viewbox.x, viewbox.y),
      toLocalPoint(viewbox.x + viewbox.width, viewbox.y),
      toLocalPoint(viewbox.x + viewbox.width, viewbox.y + viewbox.height),
      toLocalPoint(viewbox.x, viewbox.y + viewbox.height),
    ].join(' ');

    const placeholderShapeClip =
        new ClipPath().add(shapeWithoutTransform(placeholderShape));
    defs.add(placeholderShapeClip);
    clips.push(placeholderShapeClip);

    const stageClip =
        new ClipPath().add(new Polygon().plot(stagePoints));
    defs.add(stageClip);
    clips.push(stageClip);

    let stageEdgeClip = clipPath;
    if (!stageEdgeClip) {
      stageEdgeClip =
          new ClipPath().add(shapeWithoutTransform(placeholderShape));
      defs.add(stageEdgeClip);
      clips.push(stageEdgeClip);
    }

    borderContainer.add(placeholderShape);
    strokeAndClip(placeholderShape, placeholderShapeClip);

    const stageEdge = new Polygon().plot(stagePoints).fill('none');
    borderContainer.add(stageEdge);
    strokeAndClip(stageEdge, stageEdgeClip);

    borderContainer.clipWith(stageClip);
    this.borderClips = clips;
  }
}

class Transaction
{
  public active: boolean = true;

  constructor(
    private rollbackAction: () => void, private cleanAction: () => void)
  {}

  public commit(): void
  {
    this.active = false;
    this.cleanAction();
  }

  public rollback(): void
  {
    this.rollbackAction();
    this.active = false;
  }
}

export abstract class Placeholder
{
  private transactions: Array<Transaction> = [];
  private _hasFocus: boolean = false;
  private readonly wrapperGroupEventListeners: Object = {
    'click': this.wrapperClicked.bind(this),
  };
  private readonly baseEventListeners: Object = {
    'tab': this.showEditMarker.bind(this)
  };

  public wrapper: Svg;
  public group: G | undefined;

  public clicked: EventEmitter<void> = new EventEmitter<void>();
  public changed: EventEmitter<string> = new EventEmitter<string>();
  public destroyed: EventEmitter<boolean> = new EventEmitter<boolean>();

  private marker: MarkerStack;

  public abstract hasEditableContent(): boolean;
  public abstract isFlexible(): boolean;
  public abstract extractInformation(): Promise<any>;
  protected abstract determineGroup(): G | undefined;
  protected abstract handleClick(click?: ClickInfo): void;

  constructor(
      protected errorService: ErrorService,
      protected loggingService: LoggingService,
      protected designStepService: DesignStepService,
      protected galleryService: GalleryService,
      protected toolboxService: ToolboxService,
      protected placeholderContentService: PlaceholderContentService,
      protected placeholderService: PlaceholderService,
      private eventTrackingService: EventTrackingService,
      private productService: ProductService,
      public element: any,
      protected partId: string,
      public type: PlaceholderType,
      private isLocked: boolean,
      private dialog: MatDialog,
  )
  {
    if (element.parents().length === 0) {
      return undefined;
    }

    this.wrapper = element.parents()[0] as Svg;
    this.wrapper.attr('text-rendering', 'geometricPrecision');

    if (this.wrapper.parents()[0] === undefined) {
      return undefined;
    }

    this.group = this.determineGroup();

    if (this.isContentChangeable()) {
      for (const listener in this.baseEventListeners) {
        if (this.baseEventListeners.hasOwnProperty(listener)) {
          this.wrapper.node.addEventListener(
              listener, this.baseEventListeners[listener]);
        }
      }
      const wrapperGroup = this.wrapper.node.parentNode;
      for (const listener in this.wrapperGroupEventListeners) {
        if (this.wrapperGroupEventListeners.hasOwnProperty(listener)) {
          wrapperGroup.addEventListener(
              listener, this.wrapperGroupEventListeners[listener]);
        }
      }
    }

    if (this.isLinked() && !this.isContentChangeable()) {
      const linkedNode = this.group ? this.group.node : this.wrapper.node;
      linkedNode.style['pointer-events'] = 'none';
    }

    this.marker = new MarkerStack(new Marker(this.wrapper, this.type));
  }

  public async destructor(): Promise<void>
  {
    if (this.isContentChangeable()) {
      for (const listener in this.baseEventListeners) {
        if (this.baseEventListeners.hasOwnProperty(listener)) {
          this.wrapper.node.removeEventListener(
              listener, this.baseEventListeners[listener]);
        }
      }
      const wrapperGroup = this.wrapper.node.parentNode;
      for (const listener in this.wrapperGroupEventListeners) {
        if (this.wrapperGroupEventListeners.hasOwnProperty(listener)) {
          wrapperGroup.removeEventListener(
              listener, this.wrapperGroupEventListeners[listener]);
        }
      }

      this.blur();
    }
  }

  public async remove(): Promise<void>
  {
    const remove = async () => {
      this.destroyed.emit(true);
      await this.destructor();
      this.wrapper.node.remove();
      if (this.group && this.group.node.childElementCount === 0) {
        this.group.node.remove();
      }

    };
    if (this.isUnchanged()) {
      await remove();
      return;
    }

    this.dialog.open(DeletePlaceholderDialogComponent, {
      data: {
        buttons: {
          primary: Button.IGNORE_AND_CONTINUE,
          secondary: Button.CANCEL
        }
      }
    }).afterClosed().subscribe(result => {
      if (result === Button.Choice.IGNORE) {
        remove();
        this.eventTrackingService.track({
          category: this.productService.getCurrentProduct().id,
          action: 'delete_text',
          label: 'continue anyway'
        });
      }
      else {
        this.eventTrackingService.track({
          category: this.productService.getCurrentProduct().id,
          action: 'delete_text',
          label: 'cancel'
        });
      }
    });
  }

  public async reset(): Promise<void>
  {
    this.detachContent();
    this.blur();
  }

  public mouseLeave(event: MouseEvent): void
  {
    event.stopPropagation();
    this.hideHoverMarker();
    this.wrapper.node.style.cursor = 'default';
  }

  public finalize(): void
  {
    this.blur();

    for (const transaction of this.transactions.filter(el => el.active)) {
      transaction.rollback();
    }
  }

  public startTransaction(rollback: () => void): Transaction
  {
    const t = new Transaction(rollback, this.cleanTransactions.bind(this));
    this.transactions.push(t);
    return t;
  }

  public isUnchanged(): boolean
  {
    return StageUtils.getBooleanAttribute(
        this.element.node, StageConstants.ATTRIBUTE_IS_UNCHANGED);
  }

  public isContentChangeable(): boolean
  {
    if (this.isLocked) {
      return false;
    }

    return StageUtils.isContentChangeable(this.wrapper.node);
  }

  public isLinked(): boolean
  {
    const el = this.element.node;
    return el.hasAttribute(StageConstants.ATTRIBUTE_COPY_FROM) &&
        el.getAttribute(StageConstants.ATTRIBUTE_COPY_FROM) !== 'false';
  }

  public isPending(): boolean
  {
    return this.element.node.hasAttribute(StageConstants.ATTRIBUTE_PENDING);
  }

  public get zoomMode(): string
  {
    return this.wrapper.attr(StageConstants.ATTRIBUTE_ZOOM_MODE).toLowerCase();
  }

  public set(attributeName: string, attributeValue?: any): void
  {
    if (attributeValue === undefined) {
      this.removeAttribute(attributeName);
    }
    else {
      this.element.attr(attributeName, attributeValue);
    }
  }

  public get(attributeName: string): string
  {
    return this.element.attr(attributeName);
  }

  protected async focus(): Promise<void>
  {
    this.showEditMarker();

    this._hasFocus = true;
    this.hideHighlightMarker();

    this.placeholderService.setFocusedPlaceholder(this);
  }

  public blur(): void
  {
    this.hideEditMarker();
    this.hideHighlightMarker();
    this.hideHoverMarker();

    const placeholderToolbox =
        this.toolboxService.getActiveToolboxName('placeholder');
    if (placeholderToolbox) {
      this.toolboxService.cleanUp(placeholderToolbox);
    }

    if (!this.hasFocus()) {
      return;
    }
    this._hasFocus = false;
    this.placeholderService.setFocusedPlaceholder(undefined);
  }

  public async clickEvent(event?: MouseEvent | ClickInfo): Promise<void>
  {
    if (!this.isContentChangeable() || this.isPending()) {
      return;
    }

    if (event instanceof Event) {
      event.stopPropagation();
    }

    await this.focus();
    await this.handleClick(event);
    this.clicked.emit();
  }

  public getPartId(): string
  {
    return this.partId;
  }

  public async commitContent(countAsChange: boolean = true): Promise<void>
  {
    if (countAsChange) {
      this.set(StageConstants.ATTRIBUTE_IS_UNCHANGED, 'false');
    }
    await this.placeholderContentService.savePlaceholderContent(this);
  }

  public async restoreContent(): Promise<void>
  {
    const info =
        await this.placeholderContentService.restorePlaceholderContent(this);

    if (info) {
      this.set(
        StageConstants.ATTRIBUTE_IS_UNCHANGED,
        info.isUnchanged ? 'true' : 'false');
    }
  }

  public async saveContentIfChanged(): Promise<void>
  {
    if (!this.isUnchanged()) {
      await this.placeholderContentService.savePlaceholderContent(this);
    }
  }

  public hasTransformHandle(): boolean
  {
    return false;
  }

  public showHighlightMarker(): void
  {
    this.marker.highlight = true;

    setTimeout(
        this.hideHighlightMarker.bind(this), StageConstants.HIGHLIGHT_TIMEOUT);
  }

  protected detachContent(): void
  {
    this.set(StageConstants.ATTRIBUTE_IS_UNCHANGED, 'true');
  }

  protected async mouseOver(): Promise<void>
  {
    if (!this._hasFocus) {
      this.showHoverMarker();
    }
  }

  public hasFocus(): boolean
  {
    return this._hasFocus;
  }

  protected showDropMarker(): void
  {
    this.marker.drop = true;
  }

  protected hideDropMarker(): void
  {
    this.marker.drop = false;
  }

  protected showDropShroudMarker(): void
  {
    this.marker.dropshroud = true;
  }

  protected hideDropShroudMarker(): void
  {
    this.marker.dropshroud = false;
  }

  private showEditMarker(): void
  {
    this.marker.edit = true;
  }

  private showHoverMarker(): void
  {
    this.marker.hover = true;
  }

  public redrawMarker(): void
  {
    this.marker.redraw();
  }

  protected withoutMarker(fn: () => void): void
  {
    this.marker.withoutMarker(fn);
  }

  private hideEditMarker(): void
  {
    this.marker.edit = false;
  }

  private hideHoverMarker(): void
  {
    this.marker.hover = false;
  }

  private hideHighlightMarker(): void
  {
    this.marker.highlight = false;
  }

  private removeAttribute(attributeName: string): void
  {
    this.element.node.removeAttribute(attributeName);
  }

  private cleanTransactions(): void
  {
    this.transactions = this.transactions.filter(el => el.active);
  }

  private async wrapperClicked(event: MouseEvent): Promise<void>
  {
    if (! this.hasTransformHandle()) {
      await this.clickEvent(event);
    }
  }
}
